<template>
    <div>
        <v-row no-gutters>
            <v-col class="mb-2">
                <NewFilterBar
                    :show-save-button="false"
                    :available-filters="filterHelper.getAvailableFilters()"
                    :before-route-change="filterHelper.setFilterParamsIfEmpty.bind(filterHelper)"
                    @route_change="debouncedBackendQuery"
                    @before_filter_input="filterHelper.resetFiltersOnAccountChange($event)"
                >
                    <template v-slot:right="{ filters, filterAdd }">
                        <AddFilterButton
                            :filters="filters"
                            @add_filter="filterAdd($event)"
                        />

                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <icon-button
                                    v-on="on"
                                    icon="Delete"
                                    @click="filterHelper.resetFilters()"
                                />
                            </template>
                            <span>{{ $t("Clear filters") }}</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <icon-button
                                    icon="Save"
                                    :disabled="Object.keys($route.query).length < 1"
                                    @click="onSaveFiltersClick"
                                />
                            </template>
                            {{ $t("Save filters") }}
                        </v-tooltip>
                    </template>
                </NewFilterBar>
            </v-col>
        </v-row>

        <SalesInvoiceListProgress
            :selected-account="selected_account"
            :sales-invoices="processing_list"
            :get-status-color-map="getStatusColorMap"
            :update-totals="updateTotals"
        />

        <v-row
            v-if="!opening"
            align="center"
            no-gutters
        >
            <v-col>
                <v-tabs
                    v-model="selectedStatusIndex"
                    background-color="transparent"
                    class="denser-tabs"
                    slider-size="4"
                >
                    <v-tab
                        v-for="(tab, tabindex) in visibleTabs"
                        :key="'tidx' + tabindex"
                        @change="changeTab(tab, tabindex)"
                    >
                        <span class="caption font-weight-medium">
                            {{ tab.text }} <span>({{ tab.count }})</span>
                        </span>
                    </v-tab>
                </v-tabs>
            </v-col>
            <span class="mr-4 n3--text caption font-weight-medium">{{ $t('Next invoice number') }}: {{ next_invoice_number }}</span>
            <span class="mr-4 n3--text caption font-weight-medium" v-if="last_imported_at">{{ $t('Last updated') }}: {{ last_imported_at | format_datetime }}</span>

            <ColumnController
                :headers="table_props.headers"
                :visible-headers="filterHelper.getUrlParam('columns')"
                @update_columns="filterHelper.setColumns($event)"
            />
            <v-menu
                v-if="selected_account && selected_account.type === 'company' && $vuetify.breakpoint.mdAndUp"
                content-class="overflow-y-hidden dropmenu" bottom offset-y left>
                <template v-slot:activator="{ on }">
                    <icon-button
                        icon="MoreVertical"
                        :on="on"
                    />
                </template>
                <v-list dense>
                    <v-list-item
                        v-if="canChangeInvoiceNumber"
                        class="menu-list-item"
                        style="height: 32px !important; min-height: 32px !important"
                        @click="onChangeInvoiceNumber"
                    >
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ $t("Change invoice number") }}
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item
                        class="menu-list-item"
                        style="height: 32px !important; min-height: 32px !important"
                        @click="onInvoiceExportExcel"
                    >
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ $t("Invoices") + " - " + $t("Download to Excel") }}
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item
                        v-if="canExportRevenueSplit"
                        class="menu-list-item"
                        style="height: 32px !important; min-height: 32px !important"
                        @click="onRevenueSplitExportExcel"
                    >
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ $t("Revenue split") + " - " + $t("Download to Excel") }}
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>

                </v-list>
            </v-menu>
        </v-row>

        <div v-show="invoices_list.length > 0 && !opening">
            <v-row no-gutters>
                <v-col style="position: relative">
                    <BulkActionBar
                        :items="invoices_list"
                        :is-mobile="$vuetify.breakpoint.mdAndUp"
                        :selected-list-ids="selected_invoices"
                        :selected-account="selected_account"
                        :relative-elem="invoicesListElem"
                        :statuses="statuses"
                        :selected-status-index="selectedStatusIndex"
                        :selected-invoices="selected_invoices"
                        :vendor-type="vendorType"
                        :update-totals="updateTotals"
                        @selectedInvoicesUpdated="updateSelectedInvoices"
                    />
                    <v-data-table
                        ref="invoicesListElem"
                        v-model="selected_invoices"
                        :options="filterHelper.getTableOptions()"
                        :headers="visible_headers"
                        :items="invoices_list"
                        hide-default-header
                        :items-per-page="50"
                        :footer-props="table_footer_props"
                        :loading="table_props.isLoading"
                        class="elevation-2 medium-dense-table"
                        show-select
                        @update:options="filterHelper.setTableOptions($event)"
                    >
                        <template v-slot:header="{ props, on }">
                            <thead class="v-data-table-header">
                            <tr>
                                <th
                                    v-for="(header, index) in props['headers']"
                                    :key="index"
                                    :class="[formatTableHeaderClass(header, props['options'])]"
                                    :width="header.width"
                                    role="columnheader"
                                    scope="col"
                                    v-on="on"
                                    @click="
                                            header.sortable !== false &&
                                                header['value'] !== 'data-table-select'
                                                ? filterHelper.changeSort(header.value)
                                                : ''
                                        "
                                >
                                    <v-simple-checkbox
                                        v-if="header['value'] === 'data-table-select'"
                                        color="primary"
                                        :indeterminate="props.someItems && !props.everyItem"
                                        :value="props.everyItem"
                                        @input="on['toggle-select-all'](!props.someItems)"
                                    />
                                    <span
                                        v-else
                                        class="subtitle-2"
                                    >
                                            {{ header.text }}
                                        </span>
                                    <v-icon
                                        v-if="header.value === filterHelper.getUrlParam('sortBy')"
                                    >
                                        {{
                                            filterHelper.getUrlParam("sortDesc")
                                                ? "mdi-menu-down"
                                                : "mdi-menu-up"
                                        }}
                                    </v-icon>
                                </th>
                            </tr>
                            </thead>
                        </template>
                        <template v-slot:item="{ item, select, isSelected, index }">
                            <tr
                                :key="index"
                                :class="isSelected ? 'v-data-table__selected' : ''"
                                class="openable-invoice"
                                @click="onInvoiceRowClick($event, item, false)"
                                @click.middle="onInvoiceRowClick($event, item, true)"
                            >
                                <td class="pa-0">
                                    <v-simple-checkbox
                                        color="primary"
                                        class="py-2 px-4"
                                        :value="isSelected"
                                        @input="select"
                                    />
                                </td>
                                <td
                                    v-for="(header, i) in visible_headers"
                                    :key="'td_' + i"
                                    :class="'text-' + header.align"
                                >
                                    <div
                                        :class="header.class"
                                        class="main--text body-2"
                                    >
                                        <div @click.stop.prevent="">
                                            <router-link
                                                v-if="header.value === 'client.name'"
                                                :to="{ name: 'client_profile', params: { id: item.client_id }}"
                                                style="text-decoration: none"
                                            >
                                                <AccountAvatar
                                                    class="mr-3"
                                                    size="31"
                                                    tile
                                                    :initials="item.client.client_initials"
                                                    :color="item.client.color"
                                                    style="
                                                        width: min-content;
                                                        float: left;
                                                        align-self: center;
                                                        margin-right: 12px;
                                                        line-height: 38px;
                                                    "
                                                />
                                            </router-link>
                                        </div>
                                        <v-tooltip v-if="header.value === 'client.name' && item.client.name.length > 42" bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <span class="column-text-align" v-bind="attrs" v-on="on">
                                                    {{ item.client.name }}
                                                </span>
                                            </template>
                                            <span>{{ item.client.name }}</span>
                                        </v-tooltip>
                                        <span v-if=" header.value === 'client.name' && item.client" class="column-text-align">
                                            {{ item.client.name }}
                                        </span>
                                        <v-tooltip v-else-if=" header.value === 'client.groups' && getClientGroups(item.client.groups).length > 42" bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <span v-bind="attrs" v-on="on">
                                                    {{ getClientGroups(item.client.groups) }}
                                                </span>
                                            </template>
                                            <span class="tooltiptext">
                                                {{ getClientGroups(item.client.groups) }}
                                            </span>
                                        </v-tooltip>
                                        <span v-else-if="header.value === 'client.groups'">
                                            <span v-if="item.client.groups">
                                                {{ getClientGroups(item.client.groups) }}
                                            </span>
                                        </span>
                                        <span v-else-if="header.value === 'period_label'">
                                            <span v-if="item.period_label">
                                                {{ item["period_label"] | format_period_label }}
                                            </span>
                                        </span>
                                        <span v-else-if="header.value === 'invoice_date'">
                                            {{ item["invoice_date"] | format_date }}
                                        </span>
                                        <span v-else-if="header.value === 'due_date'">
                                            {{ item["due_date"] | format_date }}
                                        </span>
                                        <span v-else-if="header.value === 'net_sum'" class="text-right font-weight-medium text-no-wrap">
                                            {{ parseFloat(item["net_sum"]) | format_money(item.currency) }}
                                        </span>
                                        <span v-else-if="header.value === 'total_sum'" class="text-right font-weight-medium text-no-wrap">
                                            {{ parseFloat(item["total_sum"]) | format_money(item.currency) }}
                                        </span>
                                        <span v-else-if="header.value === 'exported_at'">
                                            {{ item["exported_at"] | format_date }}
                                        </span>
                                        <span v-else-if="header.value === 'sent_at'">
                                            {{ item["sent_at"] | format_date }}
                                        </span>
                                        <span v-else-if="header.value === 'contract_id'">
                                            {{ item["contract_id"] ? $t('Yes') : '' }}
                                        </span>
                                        <div v-else-if="header.value === 'status'" class="text-right d-inline-flex align-center">
                                            <DotStatus
                                                :color="getStatusColorMap(item.status, item.process_status).color"
                                                :text="getStatusColorMap(item.status, item.process_status).text"
                                            />
                                            <v-tooltip bottom max-width="250" v-if="getProcessErrorMessageForInvoice(item)">
                                                <template v-slot:activator="{ on }">
                                                    <div v-on="on">
                                                        <icon icon="AlertTriangle" small class="ml-1" color="error"/>
                                                    </div>
                                                </template>
                                                <span>{{ getProcessErrorMessageForInvoice(item) }}</span>
                                            </v-tooltip>
                                        </div>
                                        <span v-else class="text-no-wrap">
                                            {{ item[header.value] }}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        </template>
                        <template #body.append>
                            <tr class="g1">
                                <td></td>
                                <td
                                    v-for="(header, i) in visible_headers"
                                    :key="'td_' + i"
                                    :class="'text-' + header.align"
                                >
                                    <div
                                        v-if="header.value === 'net_sum'"
                                        :class="header.class"
                                        class="main--text font-weight-medium body-2"
                                    >
                                        <v-tooltip
                                            v-if="totalSelectedNetSumText"
                                            bottom
                                        >
                                            <template #activator="{ on }">
                                                <span v-on="on">
                                                    {{ totalSelectedNetSumText }}
                                                </span>
                                            </template>
                                            {{ $t('Selected net total') }}
                                        </v-tooltip>
                                        <v-tooltip
                                            v-else-if="totalNetSumText"
                                            bottom
                                        >
                                            <template #activator="{ on }">
                                                <span v-on="on">
                                                    {{ totalNetSumText }}
                                                </span>
                                            </template>
                                            {{ $t('Net total') }}
                                        </v-tooltip>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
            <!--            <v-row no-gutters>-->
            <!--                <v-col class="text-center">-->
            <!--                    <v-btn-->
            <!--                        v-if="current_total > invoices_list.length && current_total >= defaultItemsPerPage"-->
            <!--                        text-->
            <!--                        color="primary"-->
            <!--                        class="my-4"-->
            <!--                        :loading="loadMoreLoading"-->
            <!--                        @click="loadMore"-->
            <!--                    >-->
            <!--                        {{ $t("Show more") }}-->
            <!--                    </v-btn>-->
            <!--                </v-col>-->
            <!--            </v-row>-->
        </div>
        <!--NO INVOICES PLACEHOLDER-->
        <div
            v-if="!invoices_list.length > 0 && !opening"
            style="padding-top: 88px"
        >
            <v-row justify="center">
                <div
                    style="
            width: 151px;
            height: 151px;
            border: 1px solid #979797;
            border-radius: 50%;
          "
                >
                    <v-row
                        class="fill-height"
                        justify="center"
                    >
                        <v-icon
                            color="grey"
                            style="font-size: 88px"
                        >
                            description
                        </v-icon>
                    </v-row>
                </div>
            </v-row>
            <v-row
                class="pt-4"
                justify="center"
            >
                <span
                    style="color: var(--v-g4-base)"
                    class="headline font-weight-medium"
                >
                    {{ $t("Nothing in invoices") }}
                </span>
            </v-row>
            <v-row
                class="pt-3"
                justify="center"
            >
                <span
                    style="color: var(--v-g4-base)"
                    class="subheading"
                >
                    {{
                        $t(
                            "Please make sure you have setup recurring billing for your clients"
                        )
                    }}
                </span>
            </v-row>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
// eslint-disable-next-line no-unused-vars
import {$t, $tc} from "@/i18n";
import {Router} from "@/composables/Router";
import NewFilterBar from "../../components/NewFilterBar";
import AddFilterButton from "@/components/NewFilterBar/components/AddFilterButton";
import AccountAvatar from "../../components/AccountAvatar";
import DotStatus from "../../components/DotStatus";
import {computed, reactive, toRefs, ref, watch, onMounted, onUnmounted} from "@vue/composition-api";
import AccountModule from "../../store/modules/account";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import SalesInvoiceModule from "../../store/modules/sales_invoice";
import DialogModule from "../../store/modules/dialog";
import RightsModule from "../../composables/use-rights";
import {convertParamsObjectToQueryString, getFilterHelper} from "@/components/NewFilterBar/FilterBar";
import SendInvoicesDialog from "./SendInvoicesDialog";
import DeleteInvoicesDialog from "./DeleteInvoicesDialog";
import ExportInvoicesDialog from "./ExportInvoicesDialog";
import CreateInvoicesDialog from "./CreateInvoicesDialog";
import SalesInvoiceListProgress from "./SalesInvoiceListProgress";
import {useTable} from "@/composables/use-table";
import {getCookie} from "@/helpers/helpers";
import {debounce} from "throttle-debounce";
import ClientGroupModule from "../../store/modules/client_groups";
import ColumnController from "../../components/ColumnController";
import BulkActionBar from "../../components/BulkActionBar/BulkActionBarBilling";
import UserModule from "@/store/modules/user";
import WS from '../../utility/webSocket'
import {formatMoney} from "@/plugins/formatting"

export default {
    name: "SalesInvoicesView",
    components: {
        AccountAvatar,
        NewFilterBar,
        DotStatus,
        SalesInvoiceListProgress,
        AddFilterButton,
        ColumnController,
        BulkActionBar
    },
    setup() {
        const PROTOCOL = process.env.VUE_APP_PROTOCOL ? process.env.VUE_APP_PROTOCOL : "";
        const HOST = process.env.VUE_APP_HOST ? process.env.VUE_APP_HOST : "";
        const BASE_URL = `${PROTOCOL}://${HOST}`;

        const router = Router();
        const {is_admin_or_owner, can_edit_account, can_see_account_billing} = RightsModule();
        const {setConfirmOptions, openConfirmDialog, openDialog, setDialogOptions} = DialogModule();
        const {table_footer_props, formatTableHeaderClass} = useTable();
        const {available_accounts, isFeatureEnabled} = AccountModule();
        const {loadMembers, getVisibleMembersByCompany} = UserModule();
        const {
            fetchSalesInvoices,
            getStatusColorMap,
            getProcessErrorMessageForInvoice,
            next_invoice_number,
            last_imported_at
        } = SalesInvoiceModule();
        const totalNetSums = ref(null);
        const invoicesListElem = ref(null);
        const vendorType = ref(null);

        const canChangeInvoiceNumber = computed(() => {
            return is_admin_or_owner(active_account)
        });

        const canExportRevenueSplit = computed(() => {
            return is_admin_or_owner(active_account) && [2, 3, 4].includes(selectedStatusIndex.value)
        });

        const billing_accounts = computed(function () {
            return available_accounts.value.filter(function (x) {
                return isFeatureEnabled(x.id, "billing", false) && can_see_account_billing(x);
            });
        });

        let active_account = billing_accounts.value[0];
        if (default_account_id) {
            for (let i in billing_accounts.value) {
                if (billing_accounts.value[i].id === default_account_id) {
                    active_account = billing_accounts.value[i];
                    break;
                }
            }
        }

        const {client_groups_by_account} = ClientGroupModule();

        const getAccountClientGroups = computed(() => {
            let account = selected_account.value
            if (!account) return []
            const client_groups = client_groups_by_account(account.id);
            return client_groups.value;
        });

        const debouncedBackendQuery = debounce(300, false, () => {
            backendQuery();
        });

        const getMembers = computed(() => {
            return getVisibleMembersByCompany(active_account.id)
        })

        const defaultItemsPerPage = 50
        const filterHelper = getFilterHelper({
            filters: [
                {key: "account_id", value: [active_account.id], items: billing_accounts, default: true},
                {
                    key: "dates_list",
                    value: [
                        new Date().calcAddMonths(-1).calcMonthStart().formatSysDate(),
                        new Date().calcAddMonths(-1).calcMonthEnd().formatSysDate()
                    ],
                    extraItems: [
                        {title: $t("This month"), start: "som_0", end: "eom_0"},
                        {title: $t("Last month"), start: "som_-1", end: "eom_-1"},
                        {title: $t("This quarter"), start: "soq_0", end: "eoq_0"},
                        {title: $t("Last quarter"), start: "soq_-1", end: "eoq_-1"},
                        {title: $t("This year"), start: "soy_0", end: "eoy_0"},
                        {title: $t("Last year"), start: "soy_-1", end: "eoy_-1"},
                    ],
                    default: true
                },
                {key: "client_group_id_list", value: [], items: getAccountClientGroups, default: true},
                {key: "person_id_list", default_value: [], items: getMembers, default: true},
                {key: "default_person_id_list", default_value: [], items: getMembers, default: true},
                {key: "keyword", value: [], default: true},
            ],
            table_parameters: [
                {key: "sortBy", value: "name"},
                {key: "sortDesc", value: false},
                {key: "itemsPerPage", value: defaultItemsPerPage},
                {key: "page", value: 1},
            ],
            custom_parameters: [
                {key: "columns", value: ['client.name', 'period_label', 'invoice_number', 'due_date', 'net_sum', 'status', 'exported_at', 'sent_at']},
                {key: "invoice_status", value: ['in_progress']},
            ],
            bookmarkType: 'billing'
        })
        filterHelper.setFilterParamsIfEmpty()

        const statuses = ref([
            {id: 1, name: "in_progress", text: $t("Draft"), count: 0},
            {id: 2, name: "calculated", text: $t("Approval"), count: 0},
            {id: 3, name: "created", text: $t("Created"), count: 0},
            {id: 4, name: "sent", text: $t("Sent"), count: 0},
            {id: 5, name: "paid", text: $t("Paid"), count: 0},
            {id: 7, name: "", text: $t("All"), count: 0},
            {id: 8, name: "annulled", text: $t("Cancelled"), count: 0}
        ]);

        const invoices_list = ref([]);
        const selected_invoices = ref([]);

        const selectedStatusIndex = ref(0);

        const processing_list = ref([]);
        const state = reactive({
            panel_open: [[false], [false]],
            modal_icon: "",
            table_props: {
                items: [],
                loading: true,
                headers: [
                    {text: $t("Client"), sortable: true, value: "client.name", align: "left pl-0", width: "200px", class: "narrow-column"},
                    {text: $t("Client group"), value: "client.groups", align: "left", class: "", sortable: false},
                    {text: $t("Period"), sortable: true, value: "period_label", align: "left"},
                    {text: $t("Invoice #"), sortable: true, value: "invoice_number", align: "left"},
                    {text: $t("Invoice date"), sortable: true, value: "invoice_date", align: "left", class: ""},
                    {text: $t("Due date"), sortable: true, value: "due_date", align: "left"},
                    {text: $t("Net"), sortable: true, value: "net_sum", align: "right", class: "text-right"},
                    {text: $t("Total"), sortable: true, value: "total_sum", align: "right", class: "text-right"},
                    {text: $t("Contract"), sortable: true, value: "contract_id", align: "left", class: ""},
                    {text: $t("Exported"), sortable: true, value: "exported_at", align: "left", class: ""},
                    {text: $t("Sent"), sortable: true, value: "sent_at", align: "left", class: ""},
                    {text: $t("Status"), sortable: true, value: "status", align: "right", class: "text-right"},
                ],
                row_items: [15, 30, 60, {"text": "All", "value": -1}],
                hide_headers: false
            }
        });
        const default_account_id = getCookie("account_id");
        const loadMoreLoading = ref(false);
        const opening = ref(true);

        const setSelectedTab = function () {
            const idx = statuses.value.findIndex(i => i.name === filterHelper.getUrlParam('invoice_status'));
            if (idx > -1) {
                selectedStatusIndex.value = idx;
            } else {
                selectedStatusIndex.value = 0;
            }
        }

        // listen for import/export results
        // in case of bulk action, all actions are first performed and then results are written in quick succession, hence debouncing for 200ms should be enough
        const onMessage = debounce(200, false, () => updateTotals())

        const exportWebSocket = new WS(onMessage, "/ws/export")
        const importWebSocket = new WS(onMessage, "/ws/import")

        onMounted(() => {
            setSelectedTab()
            exportWebSocket.connect()
            importWebSocket.connect()
        });

        onUnmounted(() => {
            exportWebSocket.close()
            importWebSocket.close()
        })

        const visibleTabs = computed(function () {
            return statuses.value.filter(x => x.name !== "annulled" || x.count > 0);
        });
        const current_total = computed(function () {
            return statuses.value[selectedStatusIndex.value].count;
        });

        const selected_account = computed(() => {
            let filter_account_id = parseInt(filterHelper.getUrlParam('account_id'));
            return available_accounts.value.find(i => i.id === filter_account_id);
        });

        const backendQuery = function () {
            setSelectedTab()
            let params = filterHelper.getOnlyRelevantQueryParams();
            loadMembers()

            fetchSalesInvoices(params)
                .then(resp => {
                    // set tab totals
                    for (let i = 0; i < statuses.value.length; i++) {
                        let key = statuses.value[i].name === "" ? "all" : statuses.value[i].name;
                        statuses.value[i].count = resp.totals[key];
                    }
                    invoices_list.value = resp.items;
                    processing_list.value = resp.processing_invoices;
                    vendorType.value = resp.selected_vendor_type;
                    totalNetSums.value = resp.total_net_sums;
                    selected_invoices.value =
                        selected_invoices.value.filter(x => invoices_list.value.find(z => z.id === x.id));

                    loadMoreLoading.value = false;
                    opening.value = false;

                    // if the last cancelled invoice was restored, the tab will be hidden - re-route user to 'all' tab
                    if (visibleTabs.value.findIndex(i => i.name === filterHelper.getUrlParam('invoice_status')) < 0) {
                        const idx = visibleTabs.value.findIndex(i => i.name === "")
                        changeTab(visibleTabs.value[idx], idx)
                    }
                })
                .catch(ex => errorDialog.showError(ex));
        };

        const updateTotals = function () {
            backendQuery();
        };

        const changeTab = function (tab, tabindex) {
            if (selectedStatusIndex.value !== tabindex) {
                selectedStatusIndex.value = tabindex;

                filterHelper.setUrlParam('page', 1)
                filterHelper.setUrlParam('invoice_status', tab.name)
                debouncedBackendQuery()
            }
        };
        const loadMore = function () {
            filterHelper.setUrlParam('itemsPerPage', filterHelper.getUrlParam('itemsPerPage') + defaultItemsPerPage)
            loadMoreLoading.value = true;
        };

        const onClientClick = item => {
            return router.push({path: "/clients/" + item.client_id});
        };
        const onInvoiceRowClick = (event, invoice, middle) => {
            if (event.ctrlKey || event.metaKey || middle) {
                return window.open("/invoices/" + invoice.id);
            } else {
                return router.push("/invoices/" + invoice.id);
            }
        };
        const onExportToAccountingDialogClick = () => {
            setConfirmOptions({
                title: $t("Coming soon"),
                text: $t("Selected invoices and related product info will be sent to accounting system."),
                confirm_text: $t("Continue"),
                cancel_text: $t("Cancel"),
                action: () => {
                }
            });
            openConfirmDialog();
        }

        const visible_headers = computed(() => {
            let urlColumns = filterHelper.getUrlParam('columns')

            if (!Array.isArray(urlColumns) && urlColumns) {
                urlColumns = [urlColumns]
            }

            if (urlColumns) {
                return urlColumns.map(urlColumn => {
                    return state.table_props.headers.find(x => urlColumn === x.value)
                }).filter(x => x)
            } else {
                return []
            }
        })

        const onSaveFiltersClick = () => {
            setDialogOptions({
                component: () => import("@/views/Dialog/BookmarkDialog"),
                payload: {
                    type: "billing",
                },
            });
            openDialog();
        };

        const onChangeInvoiceNumber = () => {
            setDialogOptions({
                component: () => import("@/views/Dialog/ChangeInvoiceNumberDialog"),
                payload: {
                    type: "billing",
                    account_id: selected_account.value.id
                },
            });
            openDialog();
        };

        const updateSelectedInvoices = (invoices) => {
            selected_invoices.value = invoices
        }

        const onInvoiceExportExcel = () => {
            const invoiceIdList = invoices_list.value.map(x => x.id);
            const string_params = convertParamsObjectToQueryString({"invoice_id_list": invoiceIdList})
            window.open(BASE_URL + "/api/sales_invoices/" + selected_account.value.id + "/export/lines/excel" + string_params, "_top");
        }

        const onRevenueSplitExportExcel = () => {
            const invoiceIdList = invoices_list.value.map(x => x.id);
            const string_params = convertParamsObjectToQueryString({"invoice_id_list": invoiceIdList})
            window.open(BASE_URL + "/api/sales_invoices/" + selected_account.value.id + "/export/revenue_split/excel" + string_params, "_top");
        }

        const getClientGroups = (groups) => {
            return groups.map(x => x.group.name).join(', ')
        }

        const totalSelectedNetSumText = computed(() => {
            if (!totalNetSums.value) {
                return null
            }

            let texts = []

            let totalNetSumsByCurrencies = {}
            for (const invoice of selected_invoices.value) {
                if (!totalNetSumsByCurrencies[invoice.currency]) {
                    totalNetSumsByCurrencies[invoice.currency] = 0
                }

                totalNetSumsByCurrencies[invoice.currency] += invoice.net_sum
            }

            for (const currency of Object.keys(totalNetSumsByCurrencies)) {
                const totalNetSum = totalNetSumsByCurrencies[currency]
                const text = formatMoney(totalNetSum, currency)

                texts.push(text)
            }

            return texts.join(' / ')
        })

        const totalNetSumText = computed(() => {
            if (!totalNetSums.value) {
                return null
            }

            let texts = []

            let totalNetSumsByCurrencies = {}
            const statusName = statuses.value[selectedStatusIndex.value].name

            if (!statusName) {
                for (const invoiceStatus of Object.keys(totalNetSums.value)) {
                    const currencies = totalNetSums.value[invoiceStatus]
                    for (const currency of Object.keys(currencies)) {
                        if (!totalNetSumsByCurrencies[currency]) {
                            totalNetSumsByCurrencies[currency] = 0
                        }

                        totalNetSumsByCurrencies[currency] += totalNetSums.value[invoiceStatus][currency]
                    }
                }
            } else {
                totalNetSumsByCurrencies = totalNetSums.value[statusName]
            }

            if (!totalNetSumsByCurrencies) {
                return ''
            }

            for (const currency of Object.keys(totalNetSumsByCurrencies)) {
                const totalNetSum = totalNetSumsByCurrencies[currency]
                const text = formatMoney(totalNetSum, currency)

                texts.push(text)
            }

            return texts.join(' / ')
        })

        return {
            ...toRefs(state),
            opening,
            statuses,
            visibleTabs,
            selectedStatusIndex,
            current_total,
            table_footer_props,
            formatTableHeaderClass,
            selected_account,
            can_edit_account,
            debouncedBackendQuery,
            selected_invoices,
            invoices_list,
            processing_list,
            loadMoreLoading,
            onInvoiceRowClick,
            onExportToAccountingDialogClick,
            onClientClick,
            changeTab,
            loadMore,
            getStatusColorMap,
            updateTotals,
            filterHelper,
            visible_headers,
            onSaveFiltersClick,
            onChangeInvoiceNumber,
            next_invoice_number,
            last_imported_at,
            defaultItemsPerPage,
            invoicesListElem,
            updateSelectedInvoices,
            onInvoiceExportExcel,
            onRevenueSplitExportExcel,
            getClientGroups,
            canChangeInvoiceNumber,
            canExportRevenueSplit,
            vendorType,
            getProcessErrorMessageForInvoice,
            totalNetSumText,
            totalSelectedNetSumText,
        };
    },
};
</script>

<style scoped lang="scss">
tr {
    cursor: pointer !important;
}
</style>
